// App.jsx

import React, { useState, useEffect, useRef } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import ResetPasswordPage from "./ResetPasswordPage";
import { analyze, confirmPreCausal } from "./lib/api";

// Core UI components
import PatPublicAbout from "./components/pat_public_about";
import Logo from "./components/Logo";
import About from "./components/About";
import PrivacyDisclaimer from "./components/PrivacyDisclaimer";
import WhatPATisNot from "./components/WhatPATisNot";
import PATandBayes from "./components/PATandBayes";
import PatCausalQnAPanel from "./components/pat/PatCausalQnAPanel.jsx";
import PatCausalWorkspace from "./components/pat/PatCausalWorkspace.jsx";
import PatAtomPool from "./components/pat/PatAtomPool.jsx";
import PatPreDagWorkspace from "./components/pat/PatPreDagWorkspace.jsx";
import PatDistillationReviewPanel from "./components/pat/PatDistillationReviewPanel.jsx";
import PatOutcomeSelectionPanel from "./components/pat/PatOutcomeSelectionPanel.jsx";
import PatCausalScopePanel from "./components/pat/PatCausalScopePanel.jsx";
import PatCoarseMirrorPanel from "./components/pat/PatCoarseMirrorPanel.jsx";
import PatNonCausalPanel from "./components/pat/PatNonCausalPanel.jsx";
import PatInvalidPanel from "./components/pat/PatInvalidPanel.jsx";
import jsPDF from "jspdf";
import "katex/dist/katex.min.css";
import { signInWithEmailAndPassword, onAuthStateChanged } from "firebase/auth";
import { auth } from "./firebase";
import ResetPasswordConfirm from "./ResetPasswordConfirm";

export function downloadHistoryAsPDF(history) {
  const doc = new jsPDF();
  const pageHeight = doc.internal.pageSize.height;
  const lineHeight = 7;
  let y = 10;

  doc.setFontSize(14);
  doc.text("Probability Analysis Theory Output", 10, y);
  y += lineHeight * 2;

  history.forEach((entry, idx) => {
    doc.setFontSize(12);

    const title = `PAT Input ${idx + 1}: ${entry.input || "(No input found)"}`;
    const titleLines = doc.splitTextToSize(title, 180);
    y = addText(doc, titleLines, y, lineHeight);

    const steps = [
      ["Engine phase", entry.output?.engine_phase],
      ["Selected outcome", entry.output?.selected_outcome],
    ];

    steps.forEach(([label, text]) => {
      if (text) {
        const lines = doc.splitTextToSize(`${label}: ${text}`, 180);
        y = addText(doc, lines, y, lineHeight);
      }
    });

    const pathways =
      entry.output?.pipeline_result?.pat_illustrative_pathways?.pathways || [];

    pathways.forEach((p, i) => {
      const narrative = p?.narrative || "";
      if (narrative) {
        const lines = doc.splitTextToSize(
          `Illustrative pathway ${i + 1}: ${narrative}`,
          180,
        );
        y = addText(doc, lines, y, lineHeight);
      }
    });
  });

  // Save with a timestamped filename
  const date = new Date().toISOString().split("T")[0];
  doc.save(`pat_history_${date}.pdf`);

  // Auto-wrap and paginate
  function addText(doc, lines, y, lineHeight) {
    lines.forEach((line) => {
      if (y + lineHeight > pageHeight - 10) {
        doc.addPage();
        y = 10;
      }
      doc.text(line, 10, y);
      y += lineHeight;
    });
    return y;
  }
}

const PAT_VERSION = "PAT v4.0–20260513 | UI v5.0-20260513";

// ─────────────────────────────────────────────────────────────
// Distillation partition normaliser (UI-only defensive shim)
// Prevents clause/fragments being treated as Y (outcome)
// ─────────────────────────────────────────────────────────────
function normalisePartition(part) {
  if (!part || typeof part !== "object") return part;

  const clean = (s) =>
    String(s || "")
      .replace(/^[\s,;:.-]+/, "")
      .replace(/[\s,;:.-]+$/, "")
      .trim();

  const stripLeadingPrepositions = (s) =>
    s.replace(/^(in|on|at|for|to|of)\s+/i, "");

  const stripDisjunctionTail = (s) => s.split(/\s*,\s*or\s+/i)[0];

  const stripTrailingQuestion = (s) => s.replace(/\?\s*$/, "");

  const stripWithoutTail = (s) => s.replace(/\s+without\s+.*$/i, "");

  let Y = clean(part.Y);

  if (Y) {
    Y = stripDisjunctionTail(Y);
    Y = stripWithoutTail(Y);
    Y = stripTrailingQuestion(Y);
    Y = stripLeadingPrepositions(Y);
    Y = clean(Y);
  }

  // Guardrail: if still clause-like, force user to set Y manually in Pre-DAG
  const looksLikeClause =
    typeof Y === "string" &&
    (/\bor\b/i.test(Y) || /\bcorrelat/i.test(Y) || /\bmechanism\b/i.test(Y));

  if (looksLikeClause) Y = null;

  return { ...part, Y };
}

function parseApiResponse(data) {
  if (!data || typeof data !== "object") return { raw: data };

  let root =
    data.raw && typeof data.raw === "object" && data.raw.pipeline_result
      ? data.raw
      : data;

  let pipe = root.pipeline_result || {};

  // --------------------------------------------------
  // HARD HOIST: backend sometimes returns post-DAG
  // artefacts at top-level instead of inside pipeline_result.
  // UI expects them inside pipeline_result.
  // --------------------------------------------------
  const hoistedPipe = {
    ...pipe,
    pat_bcsm: pipe.pat_bcsm ?? root.pat_bcsm ?? null,
    pat_action_legitimacy:
      pipe.pat_action_legitimacy ?? root.pat_action_legitimacy ?? null,
    pat_illustrative_pathways: (() => {
      let pipeP = pipe?.pat_illustrative_pathways;
      let rootP = root?.pat_illustrative_pathways;

      const hasPathways = (p) =>
        Array.isArray(p?.pathways) && p.pathways.length > 0;

      // backend sometimes returns an invalid builder result
      if (pipeP?.status === "invalid_builder_output") pipeP = null;
      if (rootP?.status === "invalid_builder_output") rootP = null;

      if (hasPathways(pipeP)) return pipeP;
      if (hasPathways(rootP)) return rootP;

      // guard against backend returning {status:"missing", pathways:[]}
      if (
        (!pipeP?.pathways || pipeP.pathways.length === 0) &&
        (!rootP?.pathways || rootP.pathways.length === 0)
      ) {
        pipeP = null;
        rootP = null;
      }

      // --------------------------------------------------
      // HARD UI FALLBACK (guarantee renderable narrative)
      // --------------------------------------------------

      const causal = pipe?.pat_causal_model ?? root?.pat_causal_model ?? null;

      const vars = pipe?.pat_variables ?? root?.pat_variables ?? null;

      if (causal && vars?.outcome?.label) {
        const outcome = vars.outcome.label;

        const predictors = (vars.predictors || []).map((p) => p.label) || [];

        const narrative =
          predictors.length > 0
            ? `${predictors.join(", ")} influence ${outcome} through the assumed causal structure.`
            : `The causal model proposes mechanisms influencing ${outcome}.`;

        return {
          status: "ui_fallback",
          pathways: [
            {
              illustrative_state: "ui_fallback",
              narrative,
              steps: predictors.map((p) => `${p} → ${outcome}`),
            },
          ],
          notes:
            "Generated by UI fallback because backend returned no usable illustrative pathways.",
        };
      }

      return pipeP ?? rootP ?? null;
    })(),

    pat_interpretive_state:
      pipe.pat_interpretive_state ?? root.pat_interpretive_state ?? null,
    pat_causal_model: pipe.pat_causal_model ?? root.pat_causal_model ?? null,
    pat_claim_frame: pipe.pat_claim_frame ?? root.pat_claim_frame ?? null,
    pat_variables: (() => {
      const vars = pipe.pat_variables ?? root.pat_variables ?? null;

      if (!vars) return null;

      // normalise backend structure
      if (!vars.status) {
        return {
          status: "ok",
          ...vars,
        };
      }

      return vars;
    })(),
    pat_confounders: pipe.pat_confounders ?? root.pat_confounders ?? null,
    pat_distillation: pipe.pat_distillation ?? root.pat_distillation ?? null,
  };

  const selectedOutcome =
    pipe.selected_outcome ??
    pipe.pat_distillation?.selected_outcome ??
    root.selected_outcome ??
    null;

  return {
    ...root,

    // 🔥 RAW MUST ALWAYS BE THE CURRENT PAYLOAD ONLY
    raw: root,

    // SINGLE authority
    pipeline_result: hoistedPipe,

    // Hoisted for convenience only
    selected_outcome: selectedOutcome,
    engine_phase: pipe.engine_phase ?? null,
    confirmed_dag: pipe.confirmed_dag ?? null,
    constraints: pipe.constraints ?? null,
    status: pipe.status ?? null,

    pat_distillation: pipe.pat_distillation ?? root.pat_distillation ?? null,
  };
}

function phaseRank(phase) {
  const order = {
    loading: 0,
    pre_causal_structuring: 1,
    causal_qna: 2,
    dag_construction: 3,
    post_dag_results: 4,
  };

  return order[String(phase || "")] ?? -1;
}

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    console.error("A PAT UI component failed to render.");
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="p-4 border border-red-700 bg-red-950 text-red-200 rounded">
          <div className="text-sm font-semibold mb-2">
            Component crashed: {this.props.label || "Unknown"}
          </div>
          <div className="text-xs text-red-200/90">
            A rendering error occurred in this component. Please retry the
            analysis or refresh the page.
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// ---------------------------
// Replace a single history entry with a new parsed response
// Prefer matching by run_id (authoritative) to avoid stale UI phase.
// ---------------------------
function replaceHistoryEntryFromResponse(
  prevHistory,
  apiResponse,
  originalQuery,
) {
  const parsed =
    apiResponse?.pipeline_result && apiResponse?.selected_outcome
      ? apiResponse
      : parseApiResponse(apiResponse);

  const next = Array.isArray(prevHistory) ? [...prevHistory] : [];

  if (!next.length) {
    next.push({
      input: originalQuery,
      run_id: parsed.pipeline_run_id || null,
      output: parsed,
    });
    return next;
  }

  const lastIndex = next.length - 1;
  const prevOutput = next[lastIndex]?.output || {};

  const prevDist =
    prevOutput.pipeline_result?.pat_distillation ||
    prevOutput.pat_distillation ||
    {};

  const nextDist =
    parsed.pipeline_result?.pat_distillation || parsed.pat_distillation || {};

  let mergedDistillation;

  const nextPhase = parsed.pipeline_result?.engine_phase || parsed.engine_phase;

  const prevPhase =
    prevOutput?.pipeline_result?.engine_phase ||
    prevOutput?.engine_phase ||
    null;

  // --------------------------------------------------
  // HARD STALE-PHASE GUARD
  // Never allow an older phase payload to overwrite a
  // newer phase already present in history for the same run.
  // --------------------------------------------------
  const prevRunId =
    next[lastIndex]?.run_id ||
    prevOutput?.pipeline_result?.pipeline_run_id ||
    prevOutput?.pipeline_run_id ||
    null;

  const nextRunId =
    parsed?.pipeline_result?.pipeline_run_id || parsed?.pipeline_run_id || null;

  const nextUserPartition = nextDist?.user_partition || {};
  const hasPromotedPartition =
    (Array.isArray(nextUserPartition?.X) && nextUserPartition.X.length > 0) ||
    (Array.isArray(nextUserPartition?.M) && nextUserPartition.M.length > 0) ||
    (Array.isArray(nextUserPartition?.Z) && nextUserPartition.Z.length > 0) ||
    (typeof nextUserPartition?.Y === "string" &&
      nextUserPartition.Y.trim() !== "");

  const allowQnACompletionDowngrade =
    prevPhase === "causal_qna" &&
    nextPhase === "pre_causal_structuring" &&
    nextDist?.qna_completed === true &&
    nextDist?.status === "ready_for_pre_dag_workspace" &&
    hasPromotedPartition;

  if (
    prevRunId &&
    nextRunId &&
    prevRunId === nextRunId &&
    phaseRank(nextPhase) < phaseRank(prevPhase) &&
    !allowQnACompletionDowngrade
  ) {
    console.warn("Ignored stale phase regression.");
    return next;
  }

  if (nextPhase === "pre_causal_structuring") {
    // Only deep-merge while still inside distillation phase
    mergedDistillation = {
      ...prevDist,
      ...nextDist,
      suggested_partition: {
        ...(prevDist.suggested_partition || {}),
        ...(nextDist.suggested_partition || {}),
      },
    };
  } else {
    // Once phase advances (e.g. dag_construction), do NOT resurrect stale fields,
    // BUT preserve user_partition if it already exists.
    mergedDistillation = {
      ...nextDist,

      // Preserve user partition only if backend omitted it
      user_partition:
        nextDist?.user_partition ?? prevDist?.user_partition ?? null,

      // NEVER resurrect old suggested partitions after phase advance
      suggested_partition:
        nextDist?.suggested_partition ?? prevDist?.suggested_partition ?? null,
    };
  }

  const resolvedEnginePhase =
    phaseRank(nextPhase) >= phaseRank(prevPhase) ? nextPhase : prevPhase;

  const canonicalY =
    parsed.selected_outcome ||
    parsed.pipeline_result?.selected_outcome ||
    prevOutput.selected_outcome ||
    prevOutput.pipeline_result?.selected_outcome ||
    null;

  const mergedPipelineResult = {
    ...(prevOutput.pipeline_result || {}),
    ...(parsed.pipeline_result || {}),

    // 🔒 NEVER regress post-DAG artefacts
    pat_illustrative_pathways: (() => {
      const pipeP =
        parsed?.pipeline_result?.pat_illustrative_pathways ||
        prevOutput?.pipeline_result?.pat_illustrative_pathways ||
        null;

      const rootP =
        parsed?.pat_illustrative_pathways ||
        prevOutput?.pat_illustrative_pathways ||
        null;

      const hasUsablePathways = (p) => {
        if (!p) return false;

        if (p.status === "invalid_builder_output") return false;

        if (!Array.isArray(p.pathways) || p.pathways.length === 0) return false;

        return p.pathways.some(
          (x) => x && x.illustrative_state !== "llm_not_matched" && !x.error,
        );
      };

      if (hasUsablePathways(pipeP)) return pipeP;
      if (hasUsablePathways(rootP)) return rootP;

      // 🔒 SAFETY GUARD — preserve scaffold pathways even if LLM narratives missing
      if (pipeP?.pathways?.length || rootP?.pathways?.length) {
        return pipeP ?? rootP;
      }

      // --------------------------------------------------
      // HARD UI FALLBACK
      // Guarantee at least one illustrative pathway
      // --------------------------------------------------

      const causal =
        parsed?.pipeline_result?.pat_causal_model ||
        parsed?.pat_causal_model ||
        prevOutput?.pipeline_result?.pat_causal_model ||
        prevOutput?.pat_causal_model ||
        null;

      const vars =
        parsed?.pipeline_result?.pat_variables ||
        parsed?.pat_variables ||
        prevOutput?.pipeline_result?.pat_variables ||
        prevOutput?.pat_variables ||
        null;

      if (causal && vars?.outcome?.label) {
        const outcome = vars.outcome.label;
        const predictors = (vars.predictors || []).map((p) => p.label);

        const narrative =
          predictors.length > 0
            ? `${predictors.join(", ")} influence ${outcome} through the assumed causal structure.`
            : `The causal model proposes mechanisms influencing ${outcome}.`;

        return {
          status: "ui_fallback",
          pathways: [
            {
              illustrative_state: "ui_fallback",
              narrative,
              steps: predictors.map((p) => `${p} → ${outcome}`),
            },
          ],
          notes:
            "Generated by UI fallback because backend returned no usable illustrative pathways.",
        };
      }

      return pipeP ?? rootP ?? null;
    })(),

    pat_bcsm:
      parsed.pipeline_result?.pat_bcsm ??
      prevOutput.pipeline_result?.pat_bcsm ??
      null,

    pat_action_legitimacy:
      parsed.pipeline_result?.pat_action_legitimacy ??
      prevOutput.pipeline_result?.pat_action_legitimacy ??
      null,

    pat_interpretive_state:
      parsed.pipeline_result?.pat_interpretive_state ??
      prevOutput.pipeline_result?.pat_interpretive_state ??
      null,

    selected_outcome: canonicalY,

    pat_distillation: {
      ...mergedDistillation,
      selected_outcome: canonicalY,
      suggested_partition: {
        ...(mergedDistillation.suggested_partition || {}),
        ...(canonicalY ? { Y: canonicalY } : {}),
      },
    },
  };

  // 🔥 Authoritative partition resolution (never rebuild from partial echo)
  let part = null;

  if (mergedDistillation?.user_partition) {
    part = mergedDistillation.user_partition;
  } else if (
    mergedDistillation?.suggested_partition &&
    (Array.isArray(mergedDistillation.suggested_partition.X) ||
      Array.isArray(mergedDistillation.suggested_partition.M) ||
      Array.isArray(mergedDistillation.suggested_partition.Z))
  ) {
    part = mergedDistillation.suggested_partition;
  }

  let rebuiltVarsFromPartition = null;

  const currentPhase =
    parsed.pipeline_result?.engine_phase || parsed.engine_phase;

  const isPostDagPayload =
    currentPhase === "post_dag_results" ||
    parsed?.confirmed_dag === true ||
    parsed?.pipeline_result?.confirmed_dag === true;

  if (!isPostDagPayload && part && (part.X || part.M || part.Z || part.Y)) {
    const mode = mergedDistillation?.user_partition
      ? "user_confirmed"
      : "distillation_partition";

    rebuiltVarsFromPartition = {
      mode,

      predictors: (part.X || []).map((label, i) => ({
        id: `X${i + 1}`,
        label,
      })),
      mediators: (part.M || []).map((label, i) => ({
        id: `M${i + 1}`,
        label,
      })),
      confounders: (part.Z || []).map((label, i) => ({
        id: `Z${i + 1}`,
        label,
      })),
      outcome: part.Y ? { id: "Y", label: part.Y } : null,
    };
  }

  // 🔥 HARD RESOLVE selected_outcome (top-level must never be null if pipe has it)
  const resolvedSelectedOutcome =
    parsed?.pipeline_result?.selected_outcome ||
    parsed?.pipeline_result?.pat_distillation?.selected_outcome ||
    parsed?.selected_outcome ||
    prevOutput?.pipeline_result?.selected_outcome ||
    prevOutput?.pipeline_result?.pat_distillation?.selected_outcome ||
    prevOutput?.selected_outcome ||
    null;
  // --- normalise pat_variables contract ---
  const normalisedVars = (() => {
    const vars =
      mergedPipelineResult?.pat_variables ??
      parsed.pipeline_result?.pat_variables ??
      prevOutput.pipeline_result?.pat_variables ??
      null;

    if (!vars) return null;

    if (vars.status === "ok") return vars;

    return {
      status: "ok",
      ...vars,
    };
  })();

  next[lastIndex] = {
    ...next[lastIndex],
    input: originalQuery,
    output: {
      ...prevOutput,
      ...parsed,

      engine_phase: resolvedEnginePhase,

      // 🔥 FORCE TOP-LEVEL selected_outcome
      selected_outcome: resolvedSelectedOutcome,

      // 🔥 Top-level distillation
      pat_distillation: {
        ...mergedDistillation,
        selected_outcome:
          mergedDistillation?.selected_outcome || resolvedSelectedOutcome,
        suggested_partition: {
          ...(mergedDistillation?.suggested_partition || {}),
          ...(resolvedSelectedOutcome ? { Y: resolvedSelectedOutcome } : {}),
        },
      },

      // 🔥 CRITICAL: Override pipeline_result AFTER parsed spread
      pipeline_result: {
        ...(prevOutput.pipeline_result || {}),
        ...(parsed.pipeline_result || {}),
        ...mergedPipelineResult,

        // 🔒 Hard lock post-DAG artefacts
        engine_phase: resolvedEnginePhase,

        pat_illustrative_pathways:
          parsed.pipeline_result?.pat_illustrative_pathways ??
          parsed.pat_illustrative_pathways ??
          prevOutput.pipeline_result?.pat_illustrative_pathways ??
          prevOutput.pat_illustrative_pathways ??
          null,

        pat_bcsm:
          parsed.pipeline_result?.pat_bcsm ??
          prevOutput.pipeline_result?.pat_bcsm ??
          null,

        pat_action_legitimacy:
          parsed.pipeline_result?.pat_action_legitimacy ??
          prevOutput.pipeline_result?.pat_action_legitimacy ??
          null,

        pat_interpretive_state:
          parsed.pipeline_result?.pat_interpretive_state ??
          prevOutput.pipeline_result?.pat_interpretive_state ??
          null,

        selected_outcome:
          mergedPipelineResult?.selected_outcome || resolvedSelectedOutcome,

        pat_distillation: {
          ...mergedDistillation,
          selected_outcome:
            mergedDistillation?.selected_outcome || resolvedSelectedOutcome,
          suggested_partition: {
            ...(mergedDistillation?.suggested_partition || {}),
            ...(resolvedSelectedOutcome ? { Y: resolvedSelectedOutcome } : {}),
          },
        },

        pat_variables: rebuiltVarsFromPartition || normalisedVars,
      },

      // 🔥 Also override top-level variables
      pat_variables: rebuiltVarsFromPartition || normalisedVars,

      // 🔥 HARD OVERRIDE: raw must always reflect latest backend payload
      raw: parsed.raw || parsed,
    },
  };

  return next;
}

function isLocallyInvalidInput(value) {
  const text = String(value || "").trim();

  if (!text) return true;

  // Reject strings that are mostly symbols/noise.
  const letters = (text.match(/[A-Za-z]/g) || []).length;
  const digits = (text.match(/[0-9]/g) || []).length;
  const spaces = (text.match(/\s/g) || []).length;
  const meaningfulChars = letters + digits + spaces;
  const meaningfulRatio = meaningfulChars / Math.max(text.length, 1);

  if (text.length < 3) return true;
  if (meaningfulRatio < 0.55) return true;

  // Reject text with no plausible word token.
  const wordTokens = text.match(/[A-Za-z]{2,}/g) || [];
  if (wordTokens.length === 0) return true;

  // Reject one-token gibberish such as GNHI with symbol noise.
  if (wordTokens.length === 1 && text.length <= 16 && meaningfulRatio < 0.8) {
    return true;
  }

  return false;
}

// ─────────────────────────────────────────────────────────────────────────────
// App component: make sure NO hooks are nested.
// ─────────────────────────────────────────────────────────────────────────────

function App() {
  // ─── Top‐level hooks: __do not nest these__ ───
  const [stage, setStage] = useState("about");
  const [authenticated, setAuthenticated] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(900);
  const [sessionExpired, setSessionExpired] = useState(false);
  const [query, setQuery] = useState("");
  const [response, setResponse] = useState(null);
  const [history, setHistory] = useState([]);
  const [nonCausalDrafts, setNonCausalDrafts] = useState({});

  const [loading, setLoading] = useState(false);

  const [showAbout, setShowAbout] = useState(false);
  const [showNot, setShowNot] = useState(false);
  const [showBayes, setShowBayes] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [authEmail, setAuthEmail] = useState("");
  const [authPassword, setAuthPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showResetPage, setShowResetPage] = useState(false);

  // Firebase Auth state listener
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setAuthenticated(!!user);
    });
    return () => unsubscribe();
  }, []);

  const handleFirebaseLogin = async (e) => {
    e.preventDefault();
    try {
      const cred = await signInWithEmailAndPassword(
        auth,
        authEmail,
        authPassword,
      );
      setAuthenticated(true);
      setSessionExpired(false);
      setSecondsLeft(900);
    } catch (error) {
      console.error("FIREBASE LOGIN ERROR", {
        code: error?.code,
        message: error?.message,
        full: error,
        authAppOptions: auth?.app?.options,
      });

      const map = {
        "auth/invalid-credential": "Invalid email or password.",
        "auth/invalid-login-credentials": "Invalid email or password.",
        "auth/user-not-found": "No account found for that email.",
        "auth/wrong-password": "Invalid email or password.",
        "auth/invalid-email": "Invalid email address.",
        "auth/too-many-requests": "Too many attempts. Try again later.",
        "auth/network-request-failed": "Network error. Check your connection.",
        "auth/api-key-not-valid.-please-pass-a-valid-api-key.":
          "Firebase configuration error.",
        "auth/api-key-not-valid": "Firebase configuration error.",
      };

      alert(
        "Login failed: " +
          (map[error?.code] || error?.message || "Unknown login error."),
      );
    }
  };

  const timeoutRef = useRef(null);
  const historyEndRef = useRef(null);
  const lastPostDagAutoScrollRef = useRef(null);

  // ─── Idle‐timeout effect (fires when `authenticated` changes) ───
  useEffect(() => {
    if (!authenticated) return;

    const resetTimer = () =>
      setSecondsLeft((prev) => (prev === 900 ? prev : 900));
    ["mousemove", "keydown", "click"].forEach((e) =>
      window.addEventListener(e, resetTimer),
    );

    timeoutRef.current = setInterval(() => {
      setSecondsLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timeoutRef.current);

          auth.signOut().catch((err) => {
            console.error("Session expiry sign-out failed.", err);
          });

          setAuthenticated(false);
          setSessionExpired(true);
          setQuery("");
          setResponse(null);
          setHistory([]);

          alert("Session expired due to inactivity. Please log in again.");
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      ["mousemove", "keydown", "click"].forEach((e) =>
        window.removeEventListener(e, resetTimer),
      );
      clearInterval(timeoutRef.current);
    };
  }, [authenticated]);

  // ─── Post-DAG auto-scroll effect
  // Scroll only once when the latest run reaches completed post-DAG results.
  // Do not scroll during loading, variable editing, non-causal repair,
  // outcome selection, Pre-DAG work, AtomPool edits, or DAG editing.
  useEffect(() => {
    if (!Array.isArray(history) || history.length === 0) return;

    const latestEntry = history[history.length - 1];
    if (!latestEntry) return;

    const latestPhase = deriveUiPhase(latestEntry.output);
    if (latestPhase !== "post_dag_results") return;

    const runKey =
      latestEntry.run_id ||
      latestEntry.output?.pipeline_result?.pipeline_run_id ||
      latestEntry.output?.pipeline_run_id ||
      "latest-post-dag";

    if (lastPostDagAutoScrollRef.current === runKey) return;

    lastPostDagAutoScrollRef.current = runKey;

    if (historyEndRef.current) {
      historyEndRef.current.scrollIntoView({
        behavior: "smooth",
        block: "end",
      });
    }
  }, [history]);

  // ─── Utility: format seconds as mm:ss ───
  const formatTime = (s) =>
    `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, "0")}`;

  // ───────────────────────────────────────────────────────────
  // Complexity gate (UI-only, non-inferential)
  // ───────────────────────────────────────────────────────────
  function isComplexInput(entryOutput) {
    if (!entryOutput || typeof entryOutput !== "object") return false;

    const pipe = entryOutput.pipeline_result || {};

    // Count candidate variables if present
    const atomCount =
      pipe?.pat_vector_atoms?.atoms?.length ??
      entryOutput?.pat_vector_atoms?.atoms?.length ??
      0;

    // Detect multiple causal pathways (backend hint)
    const hasMultiplePaths =
      Array.isArray(pipe?.pat_causal_model?.paths) &&
      pipe.pat_causal_model.paths.length > 1;

    // Explicit backend signal (preferred)
    if (pipe?.complexity_flag === true) return true;

    // Heuristic UI threshold (DAG usability bound)
    if (atomCount >= 8) return true;

    if (hasMultiplePaths) return true;

    return false;
  }

  // ───────────────────────────────────────────────────────────────────────────
  //  handleSubmit (inside App, not nested)
  // ───────────────────────────────────────────────────────────────────────────

  const handleSubmit = async (eOrQuery) => {
    if (eOrQuery && eOrQuery.preventDefault) eOrQuery.preventDefault();

    const finalQuery = typeof eOrQuery === "string" ? eOrQuery : query;

    if (!finalQuery) {
      return;
    }

    // Prevent double-submit / StrictMode duplicate execution
    if (loading) {
      return;
    }

    setLoading(true);
    setResponse(null);

    const localRunId = crypto.randomUUID();

    // Replace history entirely: PAT is a single-run state machine
    setHistory([
      {
        input: finalQuery,
        run_id: localRunId,
        output: {
          engine_phase: "loading",
          pipeline_result: {
            engine_phase: "loading",
            pipeline_run_id: localRunId,
          },
          pipeline_run_id: localRunId,
        },
      },
    ]);

    try {
      const apiResponse = await analyze(finalQuery);

      const parsed = parseApiResponse(apiResponse);

      // HARD PATCH: force run identity into response so downstream updates work
      const patched = {
        ...parsed,
        pipeline_run_id: localRunId,
        raw: {
          ...(parsed.raw || {}),
          pipeline_run_id: localRunId,
        },
        pipeline_result: {
          ...(parsed.pipeline_result || {}),
          pipeline_run_id: localRunId,
        },
      };

      setResponse(patched);

      // Replace the only history entry (never append)
      setHistory([
        {
          input: finalQuery,
          run_id: localRunId,
          output: patched,
        },
      ]);

      setQuery("");
    } catch (error) {
      console.error("❌ handleSubmit error:", error);

      setResponse({
        error:
          error?.message || "Unknown error occurred. See console for details.",
      });

      // Keep history in a valid state even on failure
      setHistory([
        {
          input: finalQuery,
          run_id: localRunId,
          output: {
            error:
              error?.message ||
              "Unknown error occurred. See console for details.",
          },
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  // ───────────────────────────────────────────────────────────────────────────
  //  Render login screen if not yet authenticated
  // ───────────────────────────────────────────────────────────────────────────
  if (!authenticated) {
    // Show the ResetPasswordPage if "forgot password" is triggered
    if (showResetPage) {
      return (
        <div className="min-h-screen bg-gray-950 text-white py-12 px-4 flex flex-col items-center justify-center">
          <div className="fixed top-2 right-4 text-xs text-gray-500">
            {PAT_VERSION}
          </div>
          <Logo className="w-48 h-auto mb-6" />
          <ResetPasswordPage onBack={() => setShowResetPage(false)} />
        </div>
      );
    }

    // Default login screen
    return (
      <div className="min-h-screen bg-gray-950 text-white py-12 px-4 flex flex-col items-center justify-center">
        <div className="fixed top-2 right-4 text-xs text-gray-500">
          {PAT_VERSION}
        </div>
        <h1 className="text-3xl font-bold mb-2">Probability Analysis Theory</h1>
        <p className="text-sm text-gray-400 mb-6">
          Intellectual honesty | Causal clarity
        </p>
        <Logo className="w-48 h-auto mb-6" />
        <div className="bg-gray-900 p-6 rounded-xl shadow-md w-full max-w-sm text-left">
          <form onSubmit={handleFirebaseLogin}>
            <input
              type="email"
              className="form-element w-full mb-2"
              value={authEmail}
              onChange={(e) => setAuthEmail(e.target.value)}
              placeholder="Email"
              autoComplete="username"
              required
            />
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                className="form-element w-full mb-2 pr-14"
                value={authPassword}
                onChange={(e) => setAuthPassword(e.target.value)}
                placeholder="Password"
                autoComplete="current-password"
                required
              />
              <button
                type="button"
                className="absolute right-2 top-1/2 text-xs text-gray-500"
                style={{ transform: "translateY(-50%)" }}
                tabIndex={-1}
                onClick={() => setShowPassword((show) => !show)}
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? "Hide" : "Show"}
              </button>
            </div>
            <button
              type="submit"
              className="w-full mt-4 bg-indigo-600 hover:bg-indigo-500 text-white py-2 rounded"
            >
              Sign In
            </button>
          </form>
          <button
            onClick={() => setShowResetPage(true)}
            className="mt-3 underline text-blue-400 hover:text-blue-300 text-xs"
            type="button"
          >
            Forgot password?
          </button>
        </div>

        {/* Toggle About PAT */}
        <div className="mt-6">
          <button
            onClick={() => setShowAbout((prev) => !prev)}
            className="underline text-sm text-blue-400 hover:text-blue-300"
          >
            {showAbout ? "Hide About PAT" : "About PAT"}
          </button>
        </div>

        {showAbout && (
          <div className="mt-10 w-full">
            <PatPublicAbout />
          </div>
        )}
      </div>
    );
  }

  // ───────────────────────────────────────────────────────────────────────────
  //  Render “About” screen if we haven’t started yet
  // ───────────────────────────────────────────────────────────────────────────
  if (stage === "about") {
    return (
      <div className="text-center bg-gray-950 text-white min-h-screen flex flex-col justify-center items-center px-4">
        <div className="fixed top-2 right-4 text-xs text-gray-500">
          {PAT_VERSION}
        </div>
        <h1 className="text-3xl font-bold mb-2">
          Probability Analysis Theory (PAT)
        </h1>
        <p className="text-sm text-gray-400 mb-6">
          Intellectual honesty | Causal clarity
        </p>
        <Logo className="w-48 h-auto mb-6" />

        {/* Three info dropdowns */}
        <div className="flex flex-row justify-center space-x-8 mb-6">
          {/* Left: What is PAT? */}
          <div className="flex flex-col items-center">
            <button
              onClick={() => setShowAbout((prev) => !prev)}
              className="text-green-400 hover:text-green-300 underline text-sm"
            >
              What is PAT?
            </button>
            {showAbout && (
              <div className="max-w-xs text-left text-gray-300 text-sm space-y-4 px-4 mt-2 z-10 bg-gray-800 rounded-xl shadow-lg">
                <About />
              </div>
            )}
          </div>

          {/* Center: What PAT is Not */}
          <div className="flex flex-col items-center">
            <button
              onClick={() => setShowNot((prev) => !prev)}
              className="text-green-400 hover:text-green-300 underline text-sm"
            >
              What PAT is Not
            </button>
            {showNot && (
              <div className="max-w-xs text-left text-gray-300 text-sm space-y-4 px-4 mt-2 z-10 bg-gray-800 rounded-xl shadow-lg">
                <WhatPATisNot />
              </div>
            )}
          </div>

          {/* Right: PAT & Bayes */}
          <div className="flex flex-col items-center">
            <button
              onClick={() => setShowBayes((prev) => !prev)}
              className="text-green-400 hover:text-green-300 underline text-sm"
            >
              PAT, Causality & Bayes
            </button>
            {showBayes && (
              <div className="max-w-xs text-left text-gray-300 text-sm space-y-4 px-4 mt-2 z-10 bg-gray-800 rounded-xl shadow-lg">
                <PATandBayes />
              </div>
            )}
          </div>
        </div>
        <button
          onClick={() => setShowPrivacy((prev) => !prev)}
          className="mt-4 text-gray-400 hover:text-gray-300 underline text-sm"
        >
          Privacy & Disclaimer
        </button>

        {showPrivacy && (
          <div className="mt-4 max-w-2xl text-left text-blue-300 text-sm space-y-4 px-4">
            <PrivacyDisclaimer />
          </div>
        )}

        <button
          onClick={() => setStage("input")}
          className="mt-8 bg-indigo-600 hover:bg-indigo-500 text-white px-8 py-3 rounded-xl font-semibold"
        >
          Begin Analysis
        </button>
        <p className="mt-4 max-w-xl text-center text-sm text-gray-400 leading-snug">
          PAT Engine guides analysts through structured causal-question
          formation, DAG authorisation, and post-DAG audit reporting.
        </p>
      </div>
    );
  }

  function deriveUiPhase(entryOutput) {
    if (!entryOutput || typeof entryOutput !== "object") return null;

    if (entryOutput.error) {
      return "error";
    }

    const pipe = entryOutput.pipeline_result || {};
    const enginePhase = pipe.engine_phase || entryOutput.engine_phase || null;

    const scope = entryOutput?.pat_scope || pipe?.pat_scope || null;

    const scopeStatus =
      scope?.pat_scope_status || pipe?.pat_scope_status || null;

    const dist =
      pipe?.pat_distillation ||
      entryOutput?.pat_distillation ||
      entryOutput?.raw?.pat_distillation ||
      entryOutput?.raw?.pipeline_result?.pat_distillation ||
      null;

    const selectedOutcome =
      pipe?.selected_outcome || entryOutput?.selected_outcome || null;

    const vars = pipe?.pat_variables || entryOutput?.pat_variables || null;

    const claimFrame =
      pipe?.pat_claim_frame || entryOutput?.pat_claim_frame || null;

    if (enginePhase === "loading") {
      return "loading";
    }

    // --------------------------------------------------
    // Seed variables if backend did not produce pat_variables
    // --------------------------------------------------
    let seededVars = vars;

    // 1. Claim-frame seed (existing behaviour)
    if (!vars && claimFrame?.X && claimFrame?.Y) {
      seededVars = {
        status: "ok",
        mode: "claim_frame_seed",
        predictors: [{ id: "X1", label: claimFrame.X }],
        mediators: [],
        confounders: [],
        outcome: { id: "Y", label: claimFrame.Y },
      };
    }

    // 2. Distillation seed (ONLY trivial symbolic minimal prompts)
    const isTrivialSymbolicFrame =
      dist &&
      dist?.pipeline_hint === "symbolic_minimal_frame" &&
      Array.isArray(dist?.candidate_elements) &&
      dist.candidate_elements.length === 2 &&
      Array.isArray(dist?.suggested_partition?.X) &&
      dist.suggested_partition.X.length === 1 &&
      Array.isArray(dist?.suggested_partition?.M) &&
      dist.suggested_partition.M.length === 0 &&
      Array.isArray(dist?.suggested_partition?.Z) &&
      dist.suggested_partition.Z.length === 0 &&
      typeof dist?.suggested_partition?.Y === "string";

    if (!seededVars && isTrivialSymbolicFrame) {
      const part = dist.suggested_partition;

      seededVars = {
        status: "ok",
        mode: "distillation_seed",
        predictors: (part.X || []).map((label, i) => ({
          id: `X${i + 1}`,
          label,
        })),
        mediators: [],
        confounders: [],
        outcome: part.Y ? { id: "Y", label: part.Y } : null,
      };
    }

    const activeVars = seededVars || vars;

    const hasResolvedVariables =
      activeVars &&
      activeVars.status === "ok" &&
      activeVars.outcome &&
      typeof activeVars.outcome.label === "string" &&
      activeVars.outcome.label.trim() !== "";

    const hasReviewQuestions =
      Array.isArray(dist?.review_questions) && dist.review_questions.length > 0;

    const qnaCompleted = dist?.qna_completed === true;
    const userAcked = dist?.user_acknowledged === true;

    const hasMultipleOutcomes =
      dist &&
      Array.isArray(dist.candidate_outcomes) &&
      dist.candidate_outcomes.length > 1;

    // ─────────────────────────────────────────────
    // 1. Confirmed DAG is terminal
    // ─────────────────────────────────────────────
    if (pipe.confirmed_dag === true) {
      return "post_dag_results";
    }

    // ─────────────────────────────────────────────
    // 2. Terminal scope states
    // ─────────────────────────────────────────────
    if (
      scopeStatus === "semantic_only" ||
      scopeStatus === "association_only" ||
      scopeStatus === "out_of_scope"
    ) {
      return "scope_terminal";
    }

    // ─────────────────────────────────────────────
    // 3. Complex-pseudo causal caveat gate
    // Must run BEFORE outcome selection
    // ─────────────────────────────────────────────
    if (
      scopeStatus === "complex_pseudo_causal" &&
      scope?.allow_pipeline === false &&
      !selectedOutcome
    ) {
      return "complex_pseudo_caution";
    }

    // ─────────────────────────────────────────────
    // 4. Hard outcome-selection gate (constraint-based)
    // ─────────────────────────────────────────────
    const exactlyOneOutcomeRequired =
      entryOutput?.constraints?.exactly_one_outcome_required === true ||
      pipe?.constraints?.exactly_one_outcome_required === true;
    const claimFrameY =
      pipe?.pat_claim_frame?.Y || entryOutput?.pat_claim_frame?.Y || null;

    if (
      exactlyOneOutcomeRequired &&
      !selectedOutcome &&
      !claimFrameY &&
      Array.isArray(dist?.candidate_outcomes) &&
      dist.candidate_outcomes.length > 0
    ) {
      return "outcome_selection";
    }

    // ─────────────────────────────────────────────
    // 5. Hard outcome-selection gate
    // ─────────────────────────────────────────────
    if (dist && hasMultipleOutcomes && !selectedOutcome) {
      return "outcome_selection";
    }

    // ─────────────────────────────────────────────
    // 6. QnA must complete before anything advances
    // ─────────────────────────────────────────────
    if (enginePhase === "causal_qna" && hasReviewQuestions && !qnaCompleted) {
      return "causal_qna";
    }

    // ─────────────────────────────────────────────
    // 7. Distillation acknowledgement gate
    // ─────────────────────────────────────────────
    if (hasReviewQuestions && qnaCompleted && !userAcked) {
      return "distillation_review";
    }

    // ─────────────────────────────────────────────
    // 8. Refinement-required flows
    // ─────────────────────────────────────────────
    if (
      scopeStatus === "refinement_required" &&
      scope?.allow_pipeline === false
    ) {
      return "refinement_gate";
    }

    if (
      scopeStatus === "refinement_required" &&
      scope?.allow_pipeline === true
    ) {
      return "pre_dag_structuring";
    }

    // 9. Pre-causal structuring phase
    // Skip trivial symbolic frames like "does x cause y"
    if (enginePhase === "pre_causal_structuring") {
      const trivialSymbolicFrame =
        dist &&
        Array.isArray(dist.candidate_elements) &&
        dist.candidate_elements.length === 2 &&
        dist?.suggested_partition?.X?.length === 1 &&
        dist?.suggested_partition?.M?.length === 0 &&
        dist?.suggested_partition?.Z?.length === 0 &&
        typeof dist?.suggested_partition?.Y === "string";

      if (!trivialSymbolicFrame) {
        return "pre_dag_structuring";
      }

      // otherwise fall through to variable_selection
    }

    // ─────────────────────────────────────────────
    // 10. DAG construction phase (controlled)
    // Do NOT override unresolved distillation states
    // ─────────────────────────────────────────────
    if (enginePhase === "dag_construction") {
      return "dag_construction";
    }

    // ─────────────────────────────────────────────
    // 11. Variables resolved → allow DAG
    // Only if no distillation gates remain active
    // ─────────────────────────────────────────────
    if (
      hasResolvedVariables &&
      !hasReviewQuestions &&
      !hasMultipleOutcomes &&
      scopeStatus !== "refinement_required" &&
      dist?.user_acknowledged === true
    ) {
      return "dag_construction";
    }

    // ─────────────────────────────────────────────
    // 12. Default
    // ─────────────────────────────────────────────
    return "variable_selection";
  }

  // ───────────────────────────────────────────────────────────────────────────
  //  Finally: main “analysis” screen
  // ───────────────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gray-950 text-white pb-40 px-4 flex flex-col items-center">
      {/* PAT version in top‐right */}
      <div className="fixed top-2 right-4 text-xs text-gray-500 z-10">
        {PAT_VERSION}
      </div>

      {/* ─── Logo + Heading ─── */}
      <div className="flex items-center justify-center gap-4 mb-6 mt-4">
        <Logo className="h-14 hidden sm:block" />
        <h1 className="text-3xl font-extrabold text-indigo-400 tracking-tight leading-tight">
          Probability Analysis Theory
          <sup className="text-xs align-super">™</sup>
        </h1>
        <Logo className="h-14 hidden sm:block" />
      </div>

      {/* ─── History entries ─── */}
      {history.length > 0 && (
        <div className="w-full max-w-2xl mb-40 space-y-12 text-sm animate-fade-in">
          {history.map((entry, idx) => {
            const runKey = entry.run_id || idx;

            const pipe = entry.output?.pipeline_result || {};

            const patVars =
              pipe?.pat_variables || entry.output?.pat_variables || null;

            const atoms =
              pipe?.pat_vector_atoms?.atoms ||
              entry.output?.pat_vector_atoms?.atoms ||
              null;

            const distTop = entry.output?.pat_distillation || null;

            const distPipe = pipe?.pat_distillation || null;

            const phase = deriveUiPhase(entry.output);

            const resolvedScope =
              entry.output?.pat_scope ||
              entry.output?.pipeline_result?.pat_scope ||
              null;

            const resolvedScopeStatus = resolvedScope?.pat_scope_status || null;

            const invalidScopeStatuses = [
              "invalid_input",
              "incoherent_input",
              "unparseable_input",
              "analytically_void",
            ];

            const repairableScopeStatuses = [
              "semantic_only",
              "association_only",
              "out_of_scope",
            ];

            const isInvalidScopeGate =
              phase === "scope_terminal" &&
              (invalidScopeStatuses.includes(resolvedScopeStatus) ||
                isLocallyInvalidInput(entry.input));

            const isNonCausalRepairGate =
              phase === "refinement_gate" ||
              (phase === "scope_terminal" &&
                repairableScopeStatuses.includes(resolvedScopeStatus));
            return (
              <div
                key={idx}
                className="space-y-6 border-b-4 border-indigo-800 pb-8"
              >
                {entry.output?.ui_gate === "coarse_mirror" &&
                  entry.output?.pipeline_result?.coarse_tensor && (
                    <PatCoarseMirrorPanel
                      coarseTensor={entry.output.pipeline_result.coarse_tensor}
                      onProceed={() => {
                        confirmPreCausal({
                          resume_from: "coarse_confirmation",
                          raw_input: entry.input,
                        }).then((res) => {
                          const parsed = parseApiResponse(res);

                          setResponse(parsed);

                          setHistory((prev) =>
                            replaceHistoryEntryFromResponse(
                              prev,
                              parsed,
                              entry.input,
                            ),
                          );
                        });
                      }}
                    />
                  )}
                {/* Persistent input reference (compact) */}
                {entry.input &&
                  phase !== "loading" &&
                  !isNonCausalRepairGate &&
                  !isInvalidScopeGate &&
                  ![
                    "distillation_review",
                    "dag_construction",
                    "post_dag_results",
                  ].includes(phase) && (
                    <div className="mb-5 px-5 py-4 text-base md:text-lg leading-relaxed text-slate-400 bg-slate-900/40 border border-slate-800 rounded">
                      <span className="text-slate-500 mr-2">Input:</span>
                      <span className="text-slate-200 whitespace-pre-wrap">
                        {entry.input}
                      </span>
                    </div>
                  )}

                {/* ─── Refinement gate (repairable pseudo-causal / malformed causal input) ─── */}
                {phase === "refinement_gate" && (
                  <PatNonCausalPanel
                    input={entry.input}
                    draftKey={entry.run_id || String(idx)}
                    drafts={nonCausalDrafts}
                    setDrafts={setNonCausalDrafts}
                    onConfirmReformulation={(text) => setQuery(text)}
                  />
                )}

                {/* ─── Invalid / incoherent input terminal guard ─── */}
                {isInvalidScopeGate && (
                  <PatInvalidPanel input={entry.input} scope={resolvedScope} />
                )}

                {/* ─── Semantic / association / out-of-scope repair gate ─── */}
                {phase === "scope_terminal" &&
                  !isInvalidScopeGate &&
                  repairableScopeStatuses.includes(resolvedScopeStatus) && (
                    <PatNonCausalPanel
                      input={entry.input}
                      draftKey={entry.run_id || String(idx)}
                      drafts={nonCausalDrafts}
                      setDrafts={setNonCausalDrafts}
                      onConfirmReformulation={(text) => setQuery(text)}
                    />
                  )}

                {phase === "scope_terminal" &&
                  !isInvalidScopeGate &&
                  !repairableScopeStatuses.includes(resolvedScopeStatus) &&
                  !invalidScopeStatuses.includes(resolvedScopeStatus) && (
                    <div className="mt-6 p-4 border border-indigo-700 rounded-lg text-sm text-gray-300 space-y-4 bg-slate-900/40">
                      <div className="font-semibold text-indigo-300">
                        PAT stopped before causal modelling
                      </div>

                      <div>
                        {resolvedScope?.message ||
                          "The input did not provide enough causal structure to proceed."}
                      </div>

                      {Array.isArray(resolvedScope?.what_pat_requires) &&
                        resolvedScope.what_pat_requires.length > 0 && (
                          <div>
                            PAT requires:
                            <ul className="list-disc list-inside ml-4 mt-2 space-y-1">
                              {resolvedScope.what_pat_requires.map(
                                (item, i) => (
                                  <li key={i}>{item}</li>
                                ),
                              )}
                            </ul>
                          </div>
                        )}

                      {Array.isArray(resolvedScope?.examples_of_valid_inputs) &&
                        resolvedScope.examples_of_valid_inputs.length > 0 && (
                          <div>
                            Example causal forms:
                            <ul className="list-disc list-inside ml-4 mt-2 space-y-1">
                              {resolvedScope.examples_of_valid_inputs.map(
                                (item, i) => (
                                  <li key={i}>{item}</li>
                                ),
                              )}
                            </ul>
                          </div>
                        )}
                    </div>
                  )}

                {/* Coarse semantic mirror (complex inputs ONLY) */}
                {phase !== "complex_pseudo_caution" &&
                  isComplexInput(entry.output) &&
                  entry.output?.pipeline_result?.coarse_tensor &&
                  entry.output?.pipeline_result?.engine_phase !==
                    "pre_causal_structuring" && (
                    <PatCoarseMirrorPanel
                      coarseTensor={entry.output.pipeline_result.coarse_tensor}
                    />
                  )}

                {(() => {
                  if (phase === "error") {
                    const msg =
                      entry.output?.error ||
                      "PAT analysis could not start because the backend returned an error.";

                    const isAuthUnavailable =
                      typeof msg === "string" &&
                      msg.includes("Authentication unavailable");

                    const isMissingAuthHeader =
                      typeof msg === "string" &&
                      msg.includes("Missing Authorization header");

                    const isNetworkFailure =
                      typeof msg === "string" &&
                      (msg.includes("Network failure contacting PAT backend") ||
                        msg.includes(
                          "Network timeout contacting PAT backend",
                        ) ||
                        msg.includes("Failed to fetch"));

                    return (
                      <div className="mt-6 p-4 border border-red-700 rounded-lg text-sm text-red-200 bg-red-950/30 space-y-3">
                        <div className="font-semibold text-red-300">
                          PAT analysis could not be completed
                        </div>

                        <div>{msg}</div>

                        {isAuthUnavailable && (
                          <div className="text-red-200/90">
                            The backend authentication layer is unavailable.
                            This is a service error, not a variable-extraction
                            result.
                          </div>
                        )}

                        {isMissingAuthHeader && (
                          <div className="text-red-200/90">
                            Your session did not send a valid authorisation
                            token to the backend. Please sign in again and
                            retry.
                          </div>
                        )}

                        {isNetworkFailure && (
                          <div className="text-red-200/90">
                            The request did not reach a usable PAT backend
                            response. This is a transport or service
                            availability issue, not a causal analysis result.
                          </div>
                        )}

                        {!isAuthUnavailable &&
                          !isMissingAuthHeader &&
                          !isNetworkFailure && (
                            <div className="text-red-200/90">
                              PAT returned an error before a usable analysis
                              state could be rendered.
                            </div>
                          )}
                      </div>
                    );
                  }

                  if (phase === "loading") {
                    return (
                      <div className="mt-6 p-4 border border-indigo-700 rounded-lg text-sm text-gray-300 bg-slate-900/40 space-y-2">
                        <div className="font-semibold text-indigo-300">
                          Analysing input
                        </div>
                        <div>
                          PAT is extracting candidate variables and provisional
                          structure.
                        </div>
                      </div>
                    );
                  }
                  if (phase === "complex_pseudo_caution") {
                    return (
                      <div
                        key={entry.run_id || idx}
                        className="mt-6 p-6 border border-indigo-700 rounded-xl text-gray-300 space-y-4 bg-slate-900/40"
                      >
                        <div className="text-lg font-semibold text-indigo-300">
                          Structural clarification required
                        </div>

                        <div>
                          This input is framed as a decision problem under deep
                          uncertainty rather than a single causal estimand.
                        </div>

                        <div>
                          Any causal model built from this scenario represents a
                          modelling assumption rather than an objective causal
                          structure.
                        </div>

                        <div>
                          To proceed, you must explicitly choose an outcome (Y)
                          that the causal analysis will attempt to explain.
                        </div>

                        <button
                          className="mt-4 px-6 py-3 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white font-medium"
                          onClick={() => {
                            setHistory((prev) => {
                              const next = [...prev];
                              const current = next[idx];

                              next[idx] = {
                                ...current,
                                output: {
                                  ...current.output,
                                  pat_scope: {
                                    ...(current.output?.pat_scope || {}),
                                    allow_pipeline: true,
                                  },
                                },
                              };

                              return next;
                            });
                          }}
                        >
                          Proceed to causal modelling
                        </button>
                      </div>
                    );
                  }
                  if (phase === "outcome_selection") {
                    const dist =
                      entry.output?.pat_distillation ||
                      entry.output?.pipeline_result?.pat_distillation ||
                      entry.output?.raw?.pat_distillation ||
                      entry.output?.raw?.pipeline_result?.pat_distillation ||
                      null;

                    const runId =
                      entry?.run_id ||
                      entry?.output?.raw?.pipeline_run_id ||
                      entry?.output?.pipeline_result?.pipeline_run_id ||
                      idx;

                    return (
                      <PatOutcomeSelectionPanel
                        key="outcome_panel"
                        patDistillation={dist}
                        onSelectOutcome={async (finalOutcome) => {
                          try {
                            const rawInput =
                              entry?.input ||
                              entry?.output?.pipeline_result?.raw_input ||
                              entry?.output?.raw_input ||
                              "";

                            const basePartition = dist?.user_partition ||
                              dist?.suggested_partition || {
                                X: [],
                                M: [],
                                Z: [],
                                Y: null,
                              };

                            const editedPartition = {
                              ...basePartition,
                              X: Array.isArray(basePartition.X)
                                ? basePartition.X
                                : [],
                              M: Array.isArray(basePartition.M)
                                ? basePartition.M
                                : [],
                              Z: Array.isArray(basePartition.Z)
                                ? basePartition.Z
                                : [],
                              Y: finalOutcome,
                            };

                            const payload = {
                              raw_input: rawInput,
                              resume_from: "outcome_selection",
                              selected_outcome: finalOutcome,
                              edited_partition: editedPartition,
                            };

                            const confirmed = await confirmPreCausal(payload);

                            const parsed = parseApiResponse(confirmed);

                            const localRunId =
                              entry?.run_id || parsed?.pipeline_run_id || null;

                            if (localRunId) {
                              parsed.pipeline_run_id = localRunId;
                              parsed.raw = {
                                ...(parsed.raw || {}),
                                pipeline_run_id: localRunId,
                              };
                              parsed.pipeline_result = {
                                ...(parsed.pipeline_result || {}),
                                pipeline_run_id: localRunId,
                              };
                            }

                            // HARD PATCH: force chosen Y into distillation partition everywhere
                            const baseDist =
                              parsed.pipeline_result?.pat_distillation ||
                              parsed.pat_distillation ||
                              {};

                            const patched = {
                              ...parsed,

                              // single authoritative Y
                              selected_outcome: finalOutcome,

                              // top-level distillation (some panels read this)
                              pat_distillation: {
                                ...baseDist,
                                selected_outcome: finalOutcome,
                                user_partition: {
                                  ...(baseDist.user_partition ||
                                    baseDist.suggested_partition ||
                                    {}),
                                  Y: finalOutcome,
                                },
                                suggested_partition: {
                                  ...(baseDist.suggested_partition || {}),
                                  Y: finalOutcome,
                                },
                              },

                              // pipeline_result is the real authority
                              pipeline_result: {
                                ...(parsed.pipeline_result || {}),
                                selected_outcome: finalOutcome,

                                pat_distillation: {
                                  ...baseDist,
                                  selected_outcome: finalOutcome,
                                  user_partition: {
                                    ...(baseDist.user_partition ||
                                      baseDist.suggested_partition ||
                                      {}),
                                    Y: finalOutcome,
                                  },
                                  suggested_partition: {
                                    ...(baseDist.suggested_partition || {}),
                                    Y: finalOutcome,
                                  },
                                },
                              },
                            };

                            setResponse(patched);

                            setHistory((prev) =>
                              replaceHistoryEntryFromResponse(
                                prev,
                                patched,
                                rawInput,
                              ),
                            );
                          } catch (err) {
                            console.error("Outcome confirmation failed.", err);
                            alert(
                              "Outcome confirmation failed. Check console.",
                            );
                          }
                        }}
                        onSplitModels={(outcomes) => {
                          alert("Split models not implemented yet.");
                        }}
                      />
                    );
                  }

                  // ───────────────────────────────────────────────
                  // 1. Causal scope selection
                  // ───────────────────────────────────────────────
                  if (phase === "causal_scope_selection") {
                    return (
                      <PatCausalScopePanel
                        candidates={
                          entry.output?.pipeline_result?.estimand_selection
                            ?.candidates || []
                        }
                        onSelect={(selected) => {
                          const newHistory = [...history];

                          newHistory[idx] = {
                            ...newHistory[idx],
                            output: {
                              ...newHistory[idx].output,
                              pipeline_result: {
                                ...newHistory[idx].output.pipeline_result,
                                estimand_selection: {
                                  ...newHistory[idx].output.pipeline_result
                                    .estimand_selection,
                                  selected,
                                },
                              },
                            },
                          };

                          setHistory(newHistory);

                          confirmPreCausal({
                            resume_from: "causal_scope",
                            selected_claim: selected.summary,
                            raw_input: entry.input,
                          }).then((res) => {
                            const newResponse = parseApiResponse(res);

                            setResponse(newResponse);

                            setHistory((prev) =>
                              replaceHistoryEntryFromResponse(
                                prev,
                                res,
                                entry.input,
                              ),
                            );
                          });
                        }}
                      />
                    );
                  }

                  // ───────────────────────────────────────────────
                  // 3. Causal QnA (must not loop)
                  // ───────────────────────────────────────────────
                  if (phase === "causal_qna") {
                    const dist =
                      entry.output?.pipeline_result?.pat_distillation ||
                      entry.output?.pat_distillation ||
                      entry.output?.raw?.pat_distillation ||
                      entry.output?.raw?.pipeline_result?.pat_distillation ||
                      null;

                    return (
                      <PatCausalQnAPanel
                        patDistillation={dist}
                        onComplete={async (responses) => {
                          try {
                            const rawInput =
                              entry?.input ||
                              entry?.output?.pipeline_result?.raw_input ||
                              entry?.output?.raw_input ||
                              "";

                            const selectedOutcome =
                              responses?.selected_outcome ||
                              entry?.output?.selected_outcome ||
                              entry?.output?.pipeline_result
                                ?.selected_outcome ||
                              dist?.selected_outcome ||
                              null;

                            const qnaPayload = {
                              resume_from: "causal_qna",
                              raw_input: rawInput,
                              selected_outcome: selectedOutcome,

                              // Backend contract: causal QnA details are nested under qna_responses.
                              qna_responses: {
                                answers: responses?.answers || {},
                                user_population:
                                  responses?.user_population || {},
                                detail_inputs: responses?.detail_inputs || {},
                                detail_items: responses?.detail_items || {},
                              },

                              // Temporary legacy mirrors. Keep until backend contract is cleaned.
                              qna_user_population:
                                responses?.user_population || {},
                              qna_detail_inputs: responses?.detail_inputs || {},
                              qna_detail_items: responses?.detail_items || {},
                            };

                            const confirmed =
                              await confirmPreCausal(qnaPayload);

                            const parsed = parseApiResponse(confirmed);

                            const returnedDist =
                              parsed?.pipeline_result?.pat_distillation ||
                              parsed?.pat_distillation ||
                              {};

                            const confirmedDist =
                              confirmed?.pipeline_result?.pat_distillation ||
                              confirmed?.pat_distillation ||
                              {};

                            const qnaPop =
                              returnedDist?.qna_user_population ||
                              confirmedDist?.qna_user_population ||
                              {};

                            const returnedUserPartition =
                              returnedDist?.user_partition || {};

                            const returnedPartitionMissingXYZ =
                              (!Array.isArray(returnedUserPartition?.X) ||
                                returnedUserPartition.X.length === 0) &&
                              (!Array.isArray(returnedUserPartition?.M) ||
                                returnedUserPartition.M.length === 0) &&
                              (!Array.isArray(returnedUserPartition?.Z) ||
                                returnedUserPartition.Z.length === 0);

                            const qnaPopulationHasContent =
                              (Array.isArray(qnaPop?.X) &&
                                qnaPop.X.length > 0) ||
                              (Array.isArray(qnaPop?.M) &&
                                qnaPop.M.length > 0) ||
                              (Array.isArray(qnaPop?.Z) &&
                                qnaPop.Z.length > 0) ||
                              (Array.isArray(qnaPop?.Y) && qnaPop.Y.length > 0);

                            const needsPartitionPromotion =
                              qnaPopulationHasContent &&
                              returnedPartitionMissingXYZ;

                            const promotedPartition = needsPartitionPromotion
                              ? {
                                  X: Array.isArray(qnaPop?.X) ? qnaPop.X : [],
                                  M: Array.isArray(qnaPop?.M) ? qnaPop.M : [],
                                  Z: Array.isArray(qnaPop?.Z) ? qnaPop.Z : [],
                                  Y:
                                    (Array.isArray(qnaPop?.Y) && qnaPop.Y[0]) ||
                                    returnedDist?.selected_outcome ||
                                    parsed?.selected_outcome ||
                                    selectedOutcome ||
                                    null,
                                }
                              : returnedUserPartition;

                            const patchedAfterQnA = {
                              ...parsed,
                              pat_distillation: {
                                ...returnedDist,
                                user_partition: promotedPartition,
                                suggested_partition: {
                                  ...(returnedDist?.suggested_partition || {}),
                                  ...promotedPartition,
                                },
                              },
                              pipeline_result: {
                                ...(parsed?.pipeline_result || {}),
                                pat_distillation: {
                                  ...returnedDist,
                                  user_partition: promotedPartition,
                                  suggested_partition: {
                                    ...(returnedDist?.suggested_partition ||
                                      {}),
                                    ...promotedPartition,
                                  },
                                },
                              },
                            };

                            const localRunId =
                              entry?.run_id ||
                              patchedAfterQnA?.pipeline_run_id ||
                              null;

                            if (localRunId) {
                              patchedAfterQnA.pipeline_run_id = localRunId;
                              patchedAfterQnA.raw = {
                                ...(patchedAfterQnA.raw || {}),
                                pipeline_run_id: localRunId,
                              };
                              patchedAfterQnA.pipeline_result = {
                                ...(patchedAfterQnA.pipeline_result || {}),
                                pipeline_run_id: localRunId,
                              };
                            }

                            setResponse(patchedAfterQnA);

                            setHistory((prev) =>
                              replaceHistoryEntryFromResponse(
                                prev,
                                patchedAfterQnA,
                                rawInput,
                              ),
                            );
                          } catch (err) {
                            console.error("Causal QnA confirmation failed.");
                            alert("Q&A confirmation failed. Check console.");
                          }
                        }}
                      />
                    );
                  }

                  // ───────────────────────────────────────────────
                  // 3. Distillation review (LLM output → human acknowledgement ONLY)
                  // ───────────────────────────────────────────────
                  const dist =
                    entry.output?.pat_distillation ||
                    entry.output?.pipeline_result?.pat_distillation ||
                    entry.output?.raw?.pat_distillation ||
                    entry.output?.raw?.pipeline_result?.pat_distillation ||
                    null;
                  const distForReview = (() => {
                    if (!dist) return null;

                    const selectedOutcome =
                      dist?.selected_outcome ||
                      entry.output?.selected_outcome ||
                      entry.output?.pipeline_result?.selected_outcome ||
                      null;

                    const basePartition = normalisePartition(
                      dist?.user_partition ||
                        dist?.suggested_partition || {
                          X: [],
                          M: [],
                          Z: [],
                          Y: selectedOutcome,
                        },
                    );

                    return {
                      ...dist,
                      suggested_partition: basePartition,
                      user_partition: dist?.user_partition || basePartition,
                    };
                  })();

                  if (
                    phase === "distillation_review" &&
                    dist &&
                    (dist.status === "ready_for_pre_dag_workspace" ||
                      dist.status === "multiple_outcomes_detected")
                  ) {
                    return (
                      <PatDistillationReviewPanel
                        patDistillation={distForReview}
                        onAcknowledge={(ackPayload) => {
                          const selectedOutcome =
                            entry?.output?.selected_outcome ||
                            entry?.output?.pipeline_result?.selected_outcome ||
                            null;

                          const resolvedPartition = ackPayload?.partition ||
                            entry?.output?.pipeline_result?.pat_distillation
                              ?.user_partition ||
                            entry?.output?.pipeline_result?.pat_distillation
                              ?.suggested_partition || {
                              X: [],
                              M: [],
                              Z: [],
                              Y: selectedOutcome,
                            };
                          confirmPreCausal({
                            resume_from: "distillation_review",

                            raw_input:
                              entry?.input ||
                              entry?.output?.pipeline_result?.raw_input ||
                              entry?.output?.raw_input ||
                              "",

                            selected_outcome: selectedOutcome,
                            edited_partition: resolvedPartition,
                          }).then((res) => {
                            const parsed = parseApiResponse(res);

                            const patched = {
                              ...parsed,

                              // HARD LOCK outcome everywhere
                              selected_outcome: selectedOutcome,

                              // 🔥 CRITICAL: also patch top-level distillation object
                              pat_distillation: {
                                ...(parsed.pipeline_result?.pat_distillation ||
                                  parsed.pat_distillation ||
                                  {}),

                                user_acknowledged: true,

                                user_partition: resolvedPartition,

                                suggested_partition: {
                                  ...(parsed.pipeline_result?.pat_distillation
                                    ?.suggested_partition ||
                                    parsed.pat_distillation
                                      ?.suggested_partition ||
                                    {}),

                                  ...(resolvedPartition || {}),

                                  Y: selectedOutcome,
                                },
                              },

                              pipeline_result: {
                                ...(parsed.pipeline_result || {}),

                                // HARD LOCK outcome in pipeline_result too
                                selected_outcome: selectedOutcome,

                                pat_claim_frame: {
                                  ...(parsed.pipeline_result?.pat_claim_frame ||
                                    {}),
                                  Y: selectedOutcome,
                                },

                                pat_distillation: {
                                  ...(entry.output?.pipeline_result
                                    ?.pat_distillation ||
                                    entry.output?.pat_distillation ||
                                    {}),
                                  ...(parsed.pipeline_result
                                    ?.pat_distillation ||
                                    parsed.pat_distillation ||
                                    {}),

                                  user_acknowledged: true,

                                  user_partition: {
                                    ...(resolvedPartition || {}),
                                    Y: selectedOutcome,
                                  },

                                  suggested_partition: {
                                    ...(parsed.pipeline_result?.pat_distillation
                                      ?.suggested_partition ||
                                      parsed.pat_distillation
                                        ?.suggested_partition ||
                                      {}),

                                    ...resolvedPartition,

                                    Y: selectedOutcome,
                                  },
                                },

                                pat_variables:
                                  parsed.pipeline_result?.pat_variables ||
                                  parsed.pat_variables ||
                                  null,
                              },

                              pat_claim_frame: {
                                ...(parsed.pat_claim_frame || {}),
                                Y: selectedOutcome,
                              },

                              pat_variables:
                                parsed.pipeline_result?.pat_variables ||
                                parsed.pat_variables ||
                                null,
                            };

                            setResponse(patched);

                            setHistory((prev) =>
                              replaceHistoryEntryFromResponse(
                                prev,
                                patched,
                                entry.input,
                              ),
                            );
                          });
                        }}
                      />
                    );
                  }

                  // 4. Pre-DAG semantic stabilisation (pseudo-causal upgrade path)
                  // Render Pre-DAG workspace even when distillation status is "insufficient_candidates_detected".
                  // This is a UI shim only; it does not change backend behaviour.
                  if (phase === "pre_dag_structuring") {
                    const pipe = entry.output?.pipeline_result || {};

                    const distTop =
                      entry.output?.pat_distillation ||
                      pipe?.pat_distillation ||
                      entry.output?.raw?.pat_distillation ||
                      entry.output?.raw?.pipeline_result?.pat_distillation ||
                      null;

                    // If backend did not provide a partition, seed an empty one so the workspace can function.
                    const suggestedPartitionRaw = distTop?.user_partition ||
                      distTop?.suggested_partition || {
                        X: [],
                        M: [],
                        Z: [],
                        Y:
                          pipe?.selected_outcome ||
                          entry.output?.selected_outcome ||
                          null,
                      };

                    // UI-only defensive cleanup: prevent clause fragments from becoming Y
                    const suggestedPartition = normalisePartition(
                      suggestedPartitionRaw,
                    );

                    // HARD UI SHIM:
                    // Only create a shim when backend did not already return
                    // ready_for_pre_dag_workspace. Otherwise preserve the
                    // original object identity to avoid child-state reset loops.
                    const uiDistillation =
                      distTop?.status === "ready_for_pre_dag_workspace"
                        ? distTop
                        : distTop
                          ? {
                              ...distTop,
                              status: "ready_for_pre_dag_workspace",
                              suggested_partition: suggestedPartition,
                              user_partition:
                                distTop.user_partition || suggestedPartition,
                            }
                          : {
                              status: "ready_for_pre_dag_workspace",
                              suggested_partition: suggestedPartition,
                              user_partition: suggestedPartition,
                            };

                    return (
                      <PatPreDagWorkspace
                        patDistillation={uiDistillation}
                        onAdoptDistillation={({ X, M, Z, Y }) => {
                          const resolvedVars = {
                            status: "ok",
                            mode: "user_confirmed",

                            predictors: (X || []).map((label, i) => ({
                              id: `X${i + 1}`,
                              label,
                            })),

                            mediators: (M || []).map((label, i) => ({
                              id: `M${i + 1}`,
                              label,
                            })),

                            confounders: (Z || []).map((label, i) => ({
                              id: `Z${i + 1}`,
                              label,
                            })),

                            outcome: Y ? { id: "Y", label: Y } : null,
                          };

                          const adoptedPartition = {
                            X: X || [],
                            M: M || [],
                            Z: Z || [],
                            Y: Y || null,
                          };

                          setHistory((prev) => {
                            const next = [...prev];
                            const current = next[idx];
                            const currentPipe =
                              current.output?.pipeline_result || {};
                            const currentDist =
                              current.output?.pat_distillation ||
                              currentPipe?.pat_distillation ||
                              null;

                            next[idx] = {
                              ...current,
                              output: {
                                ...current.output,

                                // persist variables FIRST so phase transition cannot outrun them
                                pat_variables: resolvedVars,

                                selected_outcome: Y || null,

                                engine_phase: "dag_construction",

                                // persist top-level distillation safely
                                pat_distillation: currentDist
                                  ? {
                                      ...currentDist,
                                      user_partition: adoptedPartition,
                                      suggested_partition: {
                                        ...(currentDist.suggested_partition ||
                                          {}),
                                        ...adoptedPartition,
                                      },
                                      selected_outcome: Y || null,
                                    }
                                  : current.output?.pat_distillation,

                                pipeline_result: {
                                  ...currentPipe,

                                  pat_variables: resolvedVars,

                                  selected_outcome: Y || null,

                                  engine_phase: "dag_construction",

                                  // keep existing pipeline fields intact, only augment distillation
                                  pat_distillation: currentDist
                                    ? {
                                        ...currentDist,
                                        user_partition: adoptedPartition,
                                        suggested_partition: {
                                          ...(currentDist.suggested_partition ||
                                            {}),
                                          ...adoptedPartition,
                                        },
                                        selected_outcome: Y || null,
                                      }
                                    : currentPipe?.pat_distillation,

                                  pat_variables: resolvedVars,
                                },

                                // redundancy safe
                                pat_variables: resolvedVars,
                              },
                            };

                            return next;
                          });
                        }}
                      />
                    );
                  }

                  // ───────────────────────────────────────────────
                  // 4. Variable selection (or Pre-DAG swap for refinement)
                  // ───────────────────────────────────────────────
                  if (
                    phase === "variable_selection" &&
                    !entry.output?.error &&
                    entry.output?.pipeline_result?.confirmed_dag !== true
                  ) {
                    const pipelineBlocked =
                      entry.output?.pipeline_result?.constraints
                        ?.pipeline_blocked === true;
                    const scopeStatus =
                      entry.output?.pat_scope?.pat_scope_status ||
                      entry.output?.pipeline_result?.pat_scope
                        ?.pat_scope_status ||
                      null;

                    // -------------------------------------------------
                    // NORMAL CAUSAL FLOW → KEEP ATOM POOL
                    // -------------------------------------------------

                    const pipe = entry.output?.pipeline_result || {};

                    const vectorAtoms = pipe?.pat_vector_atoms?.atoms ?? [];

                    const patVars =
                      pipe?.pat_variables ||
                      entry.output?.pat_variables ||
                      null;

                    const dist =
                      pipe?.pat_distillation ||
                      entry.output?.pat_distillation ||
                      null;

                    const hasSuggestedVars = patVars && patVars.status === "ok";

                    const hasDistillationCandidates =
                      Array.isArray(dist?.candidate_elements) &&
                      dist.candidate_elements.length > 0;

                    const splitClaimFramePredictors = (text) => {
                      if (typeof text !== "string") return [];

                      return splitCandidateAtomLabel(text).map((label, i) => ({
                        id: `X${i + 1}`,
                        label,
                        role: "X",
                      }));
                    };

                    const splitCandidateAtomLabel = (label) => {
                      if (typeof label !== "string") return [];

                      const cleaned = label
                        .replace(/\s+/g, " ")
                        .replace(/^[\s,;:.-]+/, "")
                        .replace(/[\s,;:.-]+$/, "")
                        .trim();

                      if (!cleaned) return [];

                      // Conservative split only for coordinated candidate-variable labels.
                      // This catches: "fear of missing out, psychological distress"
                      // and: "fear of missing out, psychological distress and self-compassion".
                      // It avoids splitting ordinary single constructs such as
                      // "quality and safety culture" when no comma-list structure is present.
                      const hasCommaList = /,/.test(cleaned);

                      if (!hasCommaList) {
                        return [cleaned];
                      }

                      return cleaned
                        .split(/\s*,\s*|\s+\band\b\s+/i)
                        .map((s) =>
                          s
                            .replace(/^[\s,;:.-]+/, "")
                            .replace(/[\s,;:.-]+$/, "")
                            .trim(),
                        )
                        .filter(Boolean);
                    };

                    const expandCandidateAtoms = (items) => {
                      if (!Array.isArray(items)) return [];

                      const expanded = [];

                      items.forEach((a, i) => {
                        const idBase = a.atom_id ?? a.id ?? `atom-${i}`;
                        const label =
                          a.label ?? a.source_text ?? `Concept ${i + 1}`;
                        const role = a.role ?? null;

                        // Do not split nominated outcomes. A compound Y should be resolved by
                        // outcome selection, not silently fractured here.
                        if (role === "Y") {
                          expanded.push({
                            id: idBase,
                            label,
                            role,
                          });
                          return;
                        }

                        const parts = splitCandidateAtomLabel(label);

                        if (parts.length <= 1) {
                          expanded.push({
                            id: idBase,
                            label,
                            role,
                          });
                          return;
                        }

                        parts.forEach((part, j) => {
                          expanded.push({
                            id: `${idBase}-${j + 1}`,
                            label: part,
                            role,
                          });
                        });
                      });

                      return expanded;
                    };

                    return (
                      <div className="space-y-3">
                        {pipelineBlocked && (
                          <div className="p-4 border border-amber-600/60 rounded-lg text-sm text-amber-200 bg-amber-900/10 space-y-2">
                            <div className="font-semibold">
                              Caution: backend flagged this run as structurally
                              blocked
                            </div>
                            <div>
                              You can still proceed by manually defining
                              variables (X/M/Z/Y). This preserves Pearl-style
                              workflow: variables first, structure next.
                            </div>
                          </div>
                        )}

                        {!loading &&
                          vectorAtoms.length === 0 &&
                          !hasSuggestedVars &&
                          !hasDistillationCandidates && (
                            <div className="p-4 border border-indigo-700 rounded-lg text-sm text-gray-300 bg-slate-900/40 space-y-2">
                              <div className="font-semibold text-indigo-300">
                                No candidate variables detected
                              </div>
                              <div>
                                The backend could not extract candidate
                                variables from this input.
                              </div>
                              <div>
                                You may manually add variables to begin causal
                                modelling.
                              </div>
                            </div>
                          )}

                        <PatAtomPool
                          atoms={(() => {
                            // --------------------------------------------------
                            // 1. Real backend atom pool is the primary authority
                            // --------------------------------------------------
                            if (
                              Array.isArray(vectorAtoms) &&
                              vectorAtoms.length > 0
                            ) {
                              return expandCandidateAtoms(vectorAtoms);
                            }

                            // --------------------------------------------------
                            // 2. If user has already confirmed structure, show it
                            // --------------------------------------------------
                            if (patVars && patVars.status === "ok") {
                              return [
                                ...(patVars.predictors || []).map((a) => ({
                                  ...a,
                                  role: "X",
                                })),
                                ...(patVars.mediators || []).map((a) => ({
                                  ...a,
                                  role: "M",
                                })),
                                ...(patVars.confounders || []).map((a) => ({
                                  ...a,
                                  role: "Z",
                                })),
                                ...(patVars.outcome
                                  ? [
                                      {
                                        ...patVars.outcome,
                                        role: "Y",
                                      },
                                    ]
                                  : []),
                              ];
                            }

                            // --------------------------------------------------
                            // 3. Claim-frame fallback for initial_extraction
                            // If X is a conjunctive phrase, split it into candidate predictors.
                            // --------------------------------------------------
                            const claimFrame =
                              pipe?.pat_claim_frame ||
                              entry.output?.pat_claim_frame ||
                              null;

                            if (claimFrame?.X && claimFrame?.Y) {
                              const predictors = splitClaimFramePredictors(
                                claimFrame.X,
                              );

                              return [
                                ...(predictors.length > 0
                                  ? predictors
                                  : [
                                      {
                                        id: "X1",
                                        label: claimFrame.X,
                                        role: "X",
                                      },
                                    ]),
                                {
                                  id: "Y",
                                  label: claimFrame.Y,
                                  role: "Y",
                                },
                              ];
                            }

                            // --------------------------------------------------
                            // 4. Distillation fallback ONLY for trivial symbolic prompts
                            // --------------------------------------------------
                            const trivialSymbolicFrame =
                              dist &&
                              Array.isArray(dist.candidate_elements) &&
                              dist.candidate_elements.length === 2 &&
                              dist?.suggested_partition?.X?.length === 1 &&
                              dist?.suggested_partition?.M?.length === 0 &&
                              dist?.suggested_partition?.Z?.length === 0 &&
                              typeof dist?.suggested_partition?.Y === "string";

                            if (trivialSymbolicFrame) {
                              const part = dist.suggested_partition;

                              return [
                                ...(part.X || []).map((label, i) => ({
                                  id: `X${i + 1}`,
                                  label,
                                  role: "X",
                                })),
                                ...(part.M || []).map((label, i) => ({
                                  id: `M${i + 1}`,
                                  label,
                                  role: "M",
                                })),
                                ...(part.Z || []).map((label, i) => ({
                                  id: `Z${i + 1}`,
                                  label,
                                  role: "Z",
                                })),
                                ...(part.Y
                                  ? [{ id: "Y", label: part.Y, role: "Y" }]
                                  : []),
                              ];
                            }

                            // --------------------------------------------------
                            // 5. Nothing available
                            // --------------------------------------------------
                            return [];
                          })()}
                          onAtomsChange={(updatedAtoms) => {
                            setHistory((prev) => {
                              const current = prev[idx];
                              const currentAtoms =
                                current?.output?.pipeline_result
                                  ?.pat_vector_atoms?.atoms || [];

                              if (
                                JSON.stringify(currentAtoms) ===
                                JSON.stringify(updatedAtoms)
                              ) {
                                return prev;
                              }

                              const next = [...prev];

                              next[idx] = {
                                ...current,
                                output: {
                                  ...current.output,
                                  pipeline_result: {
                                    ...(current.output?.pipeline_result || {}),
                                    pat_vector_atoms: {
                                      atoms: updatedAtoms,
                                    },
                                  },
                                },
                              };

                              return next;
                            });
                          }}
                          onConfirm={({ X, M, Z, Y }) => {
                            const userPartition = {
                              X: (X || []).map((p) => p.label),
                              M: (M || []).map((m) => m.label),
                              Z: (Z || []).map((z) => z.label),
                              Y: Y || null,
                            };

                            const userVars = {
                              status: "ok",
                              mode: "user_confirmed",
                              predictors: (X || []).map((p, i) => ({
                                id: `X${i + 1}`,
                                label: p.label,
                              })),
                              mediators: (M || []).map((m, i) => ({
                                id: `M${i + 1}`,
                                label: m.label,
                              })),
                              confounders: (Z || []).map((z, i) => ({
                                id: `Z${i + 1}`,
                                label: z.label,
                              })),
                              outcome: Y ? { id: "Y", label: Y } : null,
                            };

                            const newHistory = [...history];
                            const current = newHistory[idx];
                            const currentPipe =
                              current.output?.pipeline_result || {};
                            const currentDist =
                              current.output?.pat_distillation ||
                              currentPipe?.pat_distillation ||
                              {};

                            newHistory[idx] = {
                              ...current,
                              output: {
                                ...current.output,
                                engine_phase: "dag_construction",
                                selected_outcome: Y || null,

                                pat_variables: userVars,

                                pat_distillation: {
                                  ...currentDist,
                                  user_partition: userPartition,
                                  suggested_partition: {
                                    ...(currentDist.suggested_partition || {}),
                                    ...userPartition,
                                  },
                                  selected_outcome: Y || null,
                                },

                                pipeline_result: {
                                  ...currentPipe,
                                  engine_phase: "dag_construction",
                                  selected_outcome: Y || null,

                                  pat_variables: userVars,

                                  pat_distillation: {
                                    ...currentDist,
                                    user_partition: userPartition,
                                    suggested_partition: {
                                      ...(currentDist.suggested_partition ||
                                        {}),
                                      ...userPartition,
                                    },
                                    selected_outcome: Y || null,
                                  },
                                },
                              },
                            };

                            setHistory(newHistory);
                          }}
                        />
                      </div>
                    );
                  }

                  return null;
                })()}

                {/* ─────────────────────────────────────────────── */}
                {/* DAG construction + persistence (MANDATORY)      */}
                {/* ─────────────────────────────────────────────── */}
                {(() => {
                  const shouldRenderDag =
                    phase === "dag_construction" ||
                    phase === "post_dag_results";

                  if (!shouldRenderDag) return null;

                  const pipe = entry.output?.pipeline_result || {};

                  let patVars =
                    entry.output?.pat_variables ?? pipe?.pat_variables ?? null;

                  if (!patVars) {
                    console.warn(
                      "DAG render blocked: required variables are missing.",
                    );
                    return null;
                  }

                  // enforce stable UI contract
                  patVars = {
                    status: patVars.status ?? "ok",
                    predictors: patVars.predictors ?? [],
                    mediators: patVars.mediators ?? [],
                    confounders: patVars.confounders ?? [],
                    outcome: patVars.outcome ?? null,
                  };

                  return (
                    <div className="space-y-3">
                      <ErrorBoundary label="PatCausalWorkspace">
                        <PatCausalWorkspace
                          key={entry.run_id || idx}
                          patResult={entry.output}
                          onUpdated={(updated) => {
                            setHistory((prev) =>
                              replaceHistoryEntryFromResponse(
                                prev,
                                updated,
                                entry.input,
                              ),
                            );
                          }}
                        />
                      </ErrorBoundary>
                    </div>
                  );
                })()}
              </div>
            );
          })}
        </div>
      )}

      {/* scroll target */}
      <div ref={historyEndRef} />

      {/* ─── Input Area (fixed to bottom) ─── */}
      <form
        onSubmit={handleSubmit}
        className="fixed bottom-0 left-0 w-full bg-gray-950 px-4 pt-4 pb-2 flex flex-col items-center z-10"
      >
        <div className="w-full max-w-2xl flex gap-2">
          {/* Logout Button */}
          <button
            type="button"
            onClick={async () => {
              try {
                await auth.signOut();
                setAuthenticated(false);
                setAuthEmail("");
                setAuthPassword("");
                window.location.reload(); // optional: refresh state/UI
              } catch (err) {
                alert("Logout failed: " + err.message);
              }
            }}
            className="bg-gray-700 hover:bg-red-600 text-white px-4 py-2 rounded-lg font-semibold transition"
            style={{ minWidth: "80px" }} // To match button height/width
          >
            Log Out
          </button>
          <textarea
            className="form-element w-full min-h-[3.5rem] resize-none rounded-lg"
            placeholder="Enter a causal question, claim, or decision problem..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            required
          />
          <button
            type="submit"
            disabled={loading}
            className={`px-4 py-2 rounded-lg font-semibold text-white whitespace-nowrap ${
              loading ? "bg-gray-600" : "bg-indigo-600 hover:bg-indigo-500"
            }`}
          >
            {loading ? "Processing..." : "Submit"}
          </button>
        </div>
        <div className="w-full max-w-2xl text-center mt-2 text-[0.65rem] text-gray-500">
          PAT is methodical, not fast | Processing may take around 30 seconds |
          PAT uses a static GPT model (trained to November 2024) | gpt-4o can
          make mistakes.
        </div>
      </form>

      {/* Session Timer */}
      <div className="fixed top-2 left-4 bg-gray-800 text-gray-300 px-3 py-1 rounded-md text-xs shadow z-10">
        ⏳
        <span className="text-white font-semibold">
          {formatTime(secondsLeft)}
        </span>{" "}
        left
      </div>

      {/* Feedback Button */}
      <a
        href="mailto:dat@dialectix.com.au"
        className="fixed bottom-6 right-6 z-10 bg-green-600 text-white px-4 py-2 rounded-full shadow-lg text-xs hover:bg-green-500 transition"
        title="Send feedback to Dialectix"
      >
        Feedback
      </a>
    </div>
  );
}
// Router wrapper (keeps your app + adds reset routes)
function AppWithRouter() {
  return (
    <BrowserRouter>
      <Routes>
        {/* Request a reset link (user types email) */}
        <Route
          path="/reset"
          element={<ResetPasswordPage onBack={() => window.history.back()} />}
        />

        {/* Confirm reset (clicked from email) */}
        <Route path="/auth/reset" element={<ResetPasswordConfirm />} />

        {/* Everything else -> your main app */}
        <Route path="*" element={<App />} />
      </Routes>
    </BrowserRouter>
  );
}
export default AppWithRouter;
